import React from "react";
import { Zap } from "lucide-react";
import GlobalDetailDrawer from "modules/swiftboard/components/GlobalDetailDrawer";
import LocalDateTime from "services/date-converter";
import ActiveBadge from "../../../components/ActiveBadge";
import Chip from "../../../components/Chip";
import JsonBlock from "../../../components/JsonBlock";
import LifecycleBadge from "../../../components/LifecycleBadge";
import MessageList from "../../../components/MessageList";
import { PARAM_TYPE_LABEL, RECURRENCE_OPTIONS, TRIGGER_TYPES } from "../features/trigger.constants";
import { describeTree } from "../features/trigger.utils";

function ParamList({ params }) {
  if (!params?.length) return <span className="text-xs text-text_muted">None</span>;
  return (
    <ul className="divide-y divide-border rounded-lg border border-border">
      {params.map((p) => (
        <li key={p.key} className="flex items-center justify-between gap-3 px-3 py-2 text-xs">
          <span className="min-w-0 truncate">
            <span className="font-mono text-text_primary">{p.key}</span> <span className="text-text_muted">{p.label !== p.key ? p.label : ""}</span>
          </span>
          <span className="shrink-0 text-[11px] text-text_muted">
            {PARAM_TYPE_LABEL[p.data_type] ?? p.data_type}
            {p.required ? " · required" : ""}
            {p.default !== undefined && p.default !== null ? ` · default ${String(p.default)}` : ""}
          </span>
        </li>
      ))}
    </ul>
  );
}

// Read-only view of one trigger (fetched with its source and domain event by the list page).
export default function TriggerViewDrawer({ record, onClose }) {
  const validation = record.validation_message?.split("\n").filter(Boolean) ?? [];
  const type = TRIGGER_TYPES.find((t) => t.value === record.trigger_type);
  const sections = [
    {
      title: "Trigger",
      fields: [
        { label: "Name", value: record.name },
        { label: "Code", value: <span className="font-mono">{record.code}</span> },
        { label: "Type", value: type?.label ?? record.trigger_type },
        { label: "Lifecycle", value: <LifecycleBadge status={record.status} version={record.version_no} /> },
        { label: "Source", value: record.source ? `${record.source.label} (${record.source.source_key})` : "None" },
        { label: "Module", value: record.module?.label ?? "—" },
        ...(record.trigger_type === "DATE_ANCHOR"
          ? [
              { label: "Anchor date", value: <span className="font-mono">{record.anchor_field_key}</span> },
              { label: "Recurrence", value: RECURRENCE_OPTIONS.find((o) => o.value === record.anchor_recurrence)?.label ?? "—" },
            ]
          : []),
        ...(record.trigger_type === "DOMAIN_EVENT" ? [{ label: "Domain event", value: record.domain_event ? `${record.domain_event.name} (${record.domain_event.event_key})` : "—" }] : []),
        { label: "Category", value: record.category || "—" },
        { label: "Tags", value: record.tags?.length ? <span className="flex flex-wrap gap-1">{record.tags.map((t) => <Chip key={t}>{t}</Chip>)}</span> : "—" },
        { label: "Validated", value: record.validated_at ? <LocalDateTime utcTime={record.validated_at} /> : "—" },
        { label: "Status", value: <ActiveBadge active={record.is_activated} /> },
        { label: "Description", value: record.description || "—" },
      ],
    },
    ...(validation.length ? [{ title: record.status === "ACTIVE" ? "Warnings" : "Publish problems", fields: [{ value: <MessageList tone={record.status === "ACTIVE" ? "warning" : "problem"} items={validation} /> }] }] : []),
    ...(record.trigger_type !== "BROADCAST"
      ? [
          {
            title: "Conditions",
            fields: [{ value: <p className="break-words font-mono text-[11px] text-text_primary">{describeTree(record.conditions)}</p> }, { value: <JsonBlock value={record.conditions} /> }],
          },
        ]
      : []),
    { title: `Parameters (${record.params?.length ?? 0})`, fields: [{ value: <ParamList params={record.params} /> }] },
  ];

  return <GlobalDetailDrawer open onClose={onClose} title={record.name} subtitle={record.code} icon={<Zap size={20} />} sections={sections} />;
}
